import { logger } from "../utils/logger";
import type { ThemeConfig, ThemeMode } from "../types/theme";
import { validateTheme, sanitizeTheme } from "../utils/themeValidation";
import { env } from "../config/env";

const THEME_STORAGE_KEY = "theme-customizations";
const THEME_MODE_STORAGE_KEY = "theme-mode";
const API_BASE_URL = env.API_BASE_URL;

export interface ThemeServiceError {
  type: "validation" | "storage" | "network";
  message: string;
  details?: unknown;
}

export interface ThemeSettingsResponse {
  mode: ThemeMode;
  customizations: Partial<ThemeConfig>;
  updated_at?: string;
  updated_by?: string;
}

export class ThemeService {
  // Fetch theme settings from backend API
  static async fetchThemeSettings(): Promise<ThemeSettingsResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings/theme`);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch theme settings: ${response.statusText}`,
        );
      }
      return await response.json();
    } catch (error) {
      logger.warn(
        "Failed to fetch theme from API, falling back to localStorage:",
        error,
      );
      // Fallback to localStorage
      const mode = await ThemeService.loadThemeModeLocal();
      const customizations = await ThemeService.loadCustomizationsLocal();
      return { mode, customizations };
    }
  }

  // Save theme settings to backend API (admin only)
  static async saveThemeSettingsToAPI(
    mode?: ThemeMode,
    customizations?: Partial<ThemeConfig>,
  ): Promise<ThemeSettingsResponse> {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      throw {
        type: "network",
        message: "Authentication required to save theme settings",
      } as ThemeServiceError;
    }

    const response = await fetch(`${API_BASE_URL}/api/admin/settings/theme`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ mode, customizations }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw {
        type: "network",
        message: `Failed to save theme settings: ${errorText}`,
      } as ThemeServiceError;
    }

    return await response.json();
  }

  // Save theme customizations - tries API first, falls back to localStorage
  static async saveCustomizations(
    customizations: Partial<ThemeConfig>,
  ): Promise<void> {
    // Validate theme before saving
    const validation = validateTheme(customizations);
    if (!validation.isValid) {
      throw {
        type: "validation",
        message: "Theme validation failed",
        details: validation.errors,
      } as ThemeServiceError;
    }

    // Sanitize theme data
    const sanitized = sanitizeTheme(customizations);

    // Try to save to API first
    try {
      await ThemeService.saveThemeSettingsToAPI(undefined, sanitized);
    } catch (error) {
      logger.warn("Failed to save to API, saving to localStorage:", error);
      // Fallback to localStorage
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(sanitized));
    }
  }

  // Load theme customizations - tries API first, falls back to localStorage
  static async loadCustomizations(): Promise<Partial<ThemeConfig>> {
    try {
      const settings = await ThemeService.fetchThemeSettings();
      return settings.customizations || {};
    } catch (error) {
      logger.warn("Failed to load from API, using localStorage:", error);
      return ThemeService.loadCustomizationsLocal();
    }
  }

  // Load customizations from localStorage only
  static loadCustomizationsLocal(): Promise<Partial<ThemeConfig>> {
    return new Promise((resolve) => {
      try {
        const saved = localStorage.getItem(THEME_STORAGE_KEY);
        if (!saved) {
          resolve({});
          return;
        }

        const customizations = JSON.parse(saved);
        const validation = validateTheme(customizations);
        if (!validation.isValid) {
          logger.warn("Loaded theme has validation errors:", validation.errors);
          resolve(sanitizeTheme(customizations));
          return;
        }

        resolve(customizations);
      } catch (error) {
        logger.warn(
          "Failed to load theme customizations from localStorage:",
          error,
        );
        resolve({});
      }
    });
  }

  // Save theme mode preference - tries API first, falls back to localStorage
  static async saveThemeMode(mode: ThemeMode): Promise<void> {
    try {
      await ThemeService.saveThemeSettingsToAPI(mode, undefined);
    } catch (error) {
      logger.warn("Failed to save mode to API, saving to localStorage:", error);
      localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
    }
  }

  // Load theme mode preference - tries API first, falls back to localStorage
  static async loadThemeMode(): Promise<ThemeMode> {
    try {
      const settings = await ThemeService.fetchThemeSettings();
      return settings.mode || "light";
    } catch (error) {
      logger.warn("Failed to load mode from API, using localStorage:", error);
      return ThemeService.loadThemeModeLocal();
    }
  }

  // Load theme mode from localStorage only
  static loadThemeModeLocal(): Promise<ThemeMode> {
    return new Promise((resolve) => {
      try {
        const saved = localStorage.getItem(THEME_MODE_STORAGE_KEY);
        if (saved && ["light", "dark", "system"].includes(saved)) {
          resolve(saved as ThemeMode);
        } else {
          resolve("light");
        }
      } catch (error) {
        logger.warn("Failed to load theme mode from localStorage:", error);
        resolve("light");
      }
    });
  }

  // Clear all theme data
  static clearThemeData(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        localStorage.removeItem(THEME_STORAGE_KEY);
        localStorage.removeItem(THEME_MODE_STORAGE_KEY);
        resolve();
      } catch (error) {
        reject({
          type: "storage",
          message: "Failed to clear theme data",
          details: error,
        } as ThemeServiceError);
      }
    });
  }

  // Export theme as JSON string
  static exportTheme(theme: ThemeConfig): string {
    return JSON.stringify(theme, null, 2);
  }

  // Import theme from JSON string
  static importTheme(jsonString: string): Promise<Partial<ThemeConfig>> {
    return new Promise((resolve, reject) => {
      try {
        const theme = JSON.parse(jsonString);

        // Validate imported theme
        const validation = validateTheme(theme);
        if (!validation.isValid) {
          reject({
            type: "validation",
            message: "Imported theme validation failed",
            details: validation.errors,
          } as ThemeServiceError);
          return;
        }

        resolve(theme);
      } catch (error) {
        reject({
          type: "validation",
          message: "Invalid JSON format",
          details: error,
        } as ThemeServiceError);
      }
    });
  }

  // Backup current theme to a timestamped key
  static backupCurrentTheme(
    customizations: Partial<ThemeConfig>,
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        const timestamp = new Date().toISOString();
        const backupKey = `theme-backup-${timestamp}`;

        localStorage.setItem(backupKey, JSON.stringify(customizations));
        resolve(backupKey);
      } catch (error) {
        reject({
          type: "storage",
          message: "Failed to backup theme",
          details: error,
        } as ThemeServiceError);
      }
    });
  }

  // List available theme backups
  static listThemeBackups(): Promise<
    Array<{
      key: string;
      timestamp: string;
      preview: { primaryColor: string; secondaryColor: string };
    }>
  > {
    return new Promise((resolve) => {
      try {
        const backups: Array<{
          key: string;
          timestamp: string;
          preview: { primaryColor: string; secondaryColor: string };
        }> = [];

        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith("theme-backup-")) {
            try {
              const data = localStorage.getItem(key);
              if (data) {
                const theme = JSON.parse(data);
                const timestamp = key.replace("theme-backup-", "");
                backups.push({
                  key,
                  timestamp,
                  preview: {
                    primaryColor: theme.colors?.primary?.["500"] || "#3b82f6",
                    secondaryColor:
                      theme.colors?.secondary?.["500"] || "#64748b",
                  },
                });
              }
            } catch (error) {
              logger.warn(`Failed to parse backup ${key}:`, error);
            }
          }
        }

        // Sort by timestamp (newest first)
        backups.sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        );

        resolve(backups);
      } catch (error) {
        logger.warn("Failed to list theme backups:", error);
        resolve([]);
      }
    });
  }

  // Restore theme from backup
  static restoreThemeBackup(backupKey: string): Promise<Partial<ThemeConfig>> {
    return new Promise((resolve, reject) => {
      try {
        const data = localStorage.getItem(backupKey);
        if (!data) {
          reject({
            type: "storage",
            message: "Backup not found",
            details: { backupKey },
          } as ThemeServiceError);
          return;
        }

        const theme = JSON.parse(data);

        // Validate backup theme
        const validation = validateTheme(theme);
        if (!validation.isValid) {
          logger.warn("Backup theme has validation errors:", validation.errors);
          // Return sanitized version
          resolve(sanitizeTheme(theme));
          return;
        }

        resolve(theme);
      } catch (error) {
        reject({
          type: "storage",
          message: "Failed to restore theme backup",
          details: error,
        } as ThemeServiceError);
      }
    });
  }

  // Delete theme backup
  static deleteThemeBackup(backupKey: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        localStorage.removeItem(backupKey);
        resolve();
      } catch (error) {
        reject({
          type: "storage",
          message: "Failed to delete theme backup",
          details: error,
        } as ThemeServiceError);
      }
    });
  }
}
